export interface SeriesStats {
  min: number;
  max: number;
  avg: number;
}

interface SeriesCacheEntry {
  dataRef: any[];
  configRef?: Record<string, any> | null;
  title?: string;
  defaultXField: string;
  defaultYField: string;
  detectedXField: string | null;
  xField: string;
  yField: string;
  stats: SeriesStats | null;
}

interface TableColumnsCacheEntry {
  dataRef: any[];
  columns: string[];
}

const seriesCache = new Map<string, SeriesCacheEntry>();
const tableColumnsCache = new Map<string, TableColumnsCacheEntry>();

export function detectXField(data: any[]): string | null {
  if (!data || data.length === 0) return null;
  const keys = Object.keys(data[0]);
  if (keys.length === 1 && keys[0] === 'valor') return null;
  const preferred = findPreferredField(keys, ['category', 'categoria', 'label', 'name', 'nombre']);
  if (preferred) return preferred;
  return keys.find((key) => key !== 'valor') ?? null;
}

export function calculateStats(data: any[], field: string): SeriesStats | null {
  const values = data
    .map((item) => (typeof item[field] === 'number' ? item[field] : 0))
    .filter((value) => !Number.isNaN(value));

  if (values.length === 0) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const avg = values.reduce((sum, value) => sum + value, 0) / values.length;

  return { min, max, avg };
}

export function getWidgetSeriesCache(options: {
  widgetId: string;
  data: any[];
  config?: Record<string, any> | null;
  title?: string;
  defaultXField?: string;
  defaultYField?: string;
}): SeriesCacheEntry {
  const {
    widgetId,
    data,
    config = null,
    title,
    defaultXField = 'name',
    defaultYField = 'value',
  } = options;

  const cached = seriesCache.get(widgetId);
  if (
    cached &&
    cached.dataRef === data &&
    cached.configRef === config &&
    cached.title === title &&
    cached.defaultXField === defaultXField &&
    cached.defaultYField === defaultYField
  ) {
    return cached;
  }

  const detectedXField = detectXField(data);
  const keys = data?.[0] ? Object.keys(data[0]) : [];
  const normalizedXField = normalizeField(config?.xField);
  const normalizedYField = normalizeField(config?.yField);
  const preferredXField = findPreferredField(keys, [
    'category',
    'categoria',
    'label',
    'name',
    'nombre',
    'fecha',
    'date',
  ]);
  const preferredYField = findPreferredField(keys, [
    'valor',
    'value',
    'total',
    'amount',
    'count',
  ]);
  const numericField = keys.find((key) => isNumericValue(data?.[0]?.[key]));
  const xField =
    normalizedXField ||
    preferredXField ||
    detectedXField ||
    keys[0] ||
    defaultXField;
  const yField =
    normalizedYField ||
    preferredYField ||
    numericField ||
    keys[1] ||
    defaultYField;
  const stats = data.length > 0 ? calculateStats(data, yField) : null;

  const next = {
    dataRef: data,
    configRef: config,
    title,
    defaultXField,
    defaultYField,
    detectedXField,
    xField,
    yField,
    stats,
  };

  seriesCache.set(widgetId, next);
  return next;
}

function normalizeField(value?: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function findPreferredField(keys: string[], preferred: string[]): string | null {
  const lowered = keys.map((key) => key.toLowerCase());
  for (const candidate of preferred) {
    const index = lowered.indexOf(candidate.toLowerCase());
    if (index !== -1) {
      return keys[index];
    }
  }
  return null;
}

function isNumericValue(value: unknown): boolean {
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') {
    const normalized = value.replace(',', '.');
    return normalized.trim().length > 0 && Number.isFinite(Number(normalized));
  }
  return false;
}

export function getTableColumnsCache(widgetId: string, data: any[]): string[] {
  const cached = tableColumnsCache.get(widgetId);
  if (cached && cached.dataRef === data) {
    return cached.columns;
  }

  const columns = data.length > 0 ? Object.keys(data[0]) : [];
  tableColumnsCache.set(widgetId, { dataRef: data, columns });
  return columns;
}
